import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { View, TouchableOpacity, TextInput } from "react-native";
import { AppText, AppIcon, AppAssets } from "@toothfairy/shared-ui";
import DashboardPlaceholder from "./DashboardPlaceholder";

const NODE_COLORS = {
  agent: "#16a34a",
  tool: "#6366f1",
  hook: "#8b5cf6",
  authorisation: "#d97706",
  document: "#0d9488",
  topic: "#db2777",
  job: "#0891b2",
  trigger: "#dc2626",
};

const NODE_RADII = {
  agent: 10,
  tool: 8,
  hook: 8,
  authorisation: 7,
  document: 7,
  topic: 6,
  job: 8,
  trigger: 8,
};

const ALL_TYPES = [
  "agent",
  "tool",
  "hook",
  "authorisation",
  "document",
  "topic",
  "job",
  "trigger",
];

const parseJsonArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try {
    const parsed = JSON.parse(val);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const buildGraphData = ({
  agents = [],
  agentsFunctions = [],
  execEnvs = [],
  authorisations = [],
  mrcDocuments = [],
  aiEntitiesConfig = [],
  jobs = [],
  triggers = [],
}) => {
  const nodes = [];
  const nodeMap = new Map();
  const links = [];
  const linkSet = new Set();

  const addNode = (id, name, type) => {
    if (!id || nodeMap.has(id)) return;
    const node = { id, name: name || id, type };
    nodes.push(node);
    nodeMap.set(id, node);
  };

  const addLink = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    const key = `${sourceId}->${targetId}`;
    const reverseKey = `${targetId}->${sourceId}`;
    if (linkSet.has(key) || linkSet.has(reverseKey)) return;
    linkSet.add(key);
    links.push({ source: sourceId, target: targetId });
  };

  const topics = (aiEntitiesConfig || []).filter((e) => e?.type === "topic");

  agents.forEach((a) => addNode(a.id, a.label || a.name, "agent"));
  agentsFunctions.forEach((f) => addNode(f.id, f.name, "tool"));
  execEnvs.forEach((e) => addNode(e.id, e.name, "hook"));
  authorisations.forEach((a) => addNode(a.id, a.name, "authorisation"));
  mrcDocuments.forEach((d) => addNode(d.id, d.title || d.name, "document"));
  topics.forEach((t) => addNode(t.id, t.label || t.name, "topic"));
  // Scheduled jobs + event triggers are namespace-prefixed so their UUIDs can
  // never collide with agent/tool/... node ids. They link to their bound agent.
  jobs.forEach((j) => addNode(`job:${j.id}`, j.name, "job"));
  triggers.forEach((tr) => addNode(`trigger:${tr.id}`, tr.name, "trigger"));

  const validIds = new Set(nodes.map((n) => n.id));

  agents.forEach((a) => {
    if (!a?.id) return;
    parseJsonArray(a?.agentFunctions).forEach((fnId) => {
      if (validIds.has(fnId)) addLink(a.id, fnId);
    });
    parseJsonArray(a?.customCodeExecutionEnvironments).forEach((envId) => {
      if (validIds.has(envId)) addLink(a.id, envId);
    });
    parseJsonArray(a?.allowedTopics).forEach((topicId) => {
      const tid = typeof topicId === "object" ? topicId?.id : topicId;
      if (validIds.has(tid)) addLink(a.id, tid);
    });
  });

  agentsFunctions.forEach((f) => {
    if (!f?.id) return;
    if (f?.authorisationID && validIds.has(f.authorisationID)) {
      addLink(f.id, f.authorisationID);
    }
    if (
      f?.codeExecutionEnvironmentID &&
      validIds.has(f.codeExecutionEnvironmentID)
    ) {
      addLink(f.id, f.codeExecutionEnvironmentID);
    }
  });

  execEnvs.forEach((e) => {
    if (!e?.id) return;
    parseJsonArray(e?.customExecutionSecrets).forEach((authId) => {
      if (validIds.has(authId)) addLink(e.id, authId);
    });
  });

  mrcDocuments.forEach((d) => {
    if (!d?.id) return;
    parseJsonArray(d?.topics).forEach((topicId) => {
      const tid = typeof topicId === "object" ? topicId?.id : topicId;
      if (validIds.has(tid)) addLink(d.id, tid);
    });
  });

  // Agent <-> scheduled job / event trigger connections. Only link when the
  // bound agent actually exists as a node (a deleted/invalid agentID would
  // otherwise point the force-link at a missing node and break the simulation).
  jobs.forEach((j) => {
    if (j?.id && j?.agentID && validIds.has(j.agentID)) {
      addLink(j.agentID, `job:${j.id}`);
    }
  });
  triggers.forEach((tr) => {
    if (tr?.id && tr?.agentID && validIds.has(tr.agentID)) {
      addLink(tr.agentID, `trigger:${tr.id}`);
    }
  });

  const adjacency = new Map();
  links.forEach((l) => {
    const s = l.source;
    const t = l.target;
    if (!adjacency.has(s)) adjacency.set(s, new Set());
    if (!adjacency.has(t)) adjacency.set(t, new Set());
    adjacency.get(s).add(t);
    adjacency.get(t).add(s);
  });

  const connectionCounts = new Map();
  links.forEach((l) => {
    connectionCounts.set(l.source, (connectionCounts.get(l.source) || 0) + 1);
    connectionCounts.set(l.target, (connectionCounts.get(l.target) || 0) + 1);
  });

  nodes.forEach((n) => {
    n.connections = connectionCounts.get(n.id) || 0;
  });

  return { nodes, links, adjacency };
};

const computeNLevelConnections = (seedIds, adjacency, maxDepth) => {
  const result = new Set(seedIds);
  let frontier = [...seedIds];
  for (let depth = 0; depth < maxDepth; depth++) {
    const nextFrontier = [];
    for (const id of frontier) {
      const neighbors = adjacency.get(id);
      if (neighbors) {
        for (const neighborId of neighbors) {
          if (!result.has(neighborId)) {
            result.add(neighborId);
            nextFrontier.push(neighborId);
          }
        }
      }
    }
    frontier = nextFrontier;
  }
  return result;
};

const BirdsEyeView = ({
  agents = [],
  agentsFunctions = [],
  execEnvs = [],
  authorisations = [],
  mrcDocuments = [],
  aiEntitiesConfig = [],
  jobs = [],
  triggers = [],
}) => {
  const { t } = useTranslation();
  const { currentTheme, mode } = AppTheme.useTheme();
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [highlightedNode, setHighlightedNode] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [hiddenTypes, setHiddenTypes] = useState(new Set());

  const graphData = useMemo(
    () =>
      buildGraphData({
        agents,
        agentsFunctions,
        execEnvs,
        authorisations,
        mrcDocuments,
        aiEntitiesConfig,
        jobs,
        triggers,
      }),
    [
      JSON.stringify(agents?.map((a) => a?.id)),
      JSON.stringify(agentsFunctions?.map((f) => f?.id)),
      JSON.stringify(execEnvs?.map((e) => e?.id)),
      JSON.stringify(authorisations?.map((a) => a?.id)),
      JSON.stringify(mrcDocuments?.map((d) => d?.id)),
      JSON.stringify(
        aiEntitiesConfig?.filter((e) => e?.type === "topic")?.map((t) => t?.id),
      ),
      JSON.stringify(jobs?.map((j) => j?.id)),
      JSON.stringify(triggers?.map((tr) => tr?.id)),
    ],
  );

  const { nodes: allNodes, links: allLinks, adjacency } = graphData;
  const hasData = allNodes.length > 0;

  const filteredGraph = useMemo(() => {
    let visibleNodeIds = new Set(allNodes.map((n) => n.id));

    if (hiddenTypes.size > 0) {
      visibleNodeIds = new Set(
        allNodes.filter((n) => !hiddenTypes.has(n.type)).map((n) => n.id),
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const matchingNodes = allNodes.filter(
        (n) => n.name.toLowerCase().includes(query) && visibleNodeIds.has(n.id),
      );
      if (matchingNodes.length > 0) {
        const seedIds = matchingNodes.map((n) => n.id);
        const connectedIds = computeNLevelConnections(seedIds, adjacency, 3);
        visibleNodeIds = new Set(
          [...connectedIds].filter((id) => {
            const node = allNodes.find((n) => n.id === id);
            return node && !hiddenTypes.has(node.type);
          }),
        );
      } else {
        visibleNodeIds = new Set();
      }
    }

    const visibleNodes = allNodes.filter((n) => visibleNodeIds.has(n.id));
    const visibleLinks = allLinks.filter((l) => {
      const sId = typeof l.source === "object" ? l.source.id : l.source;
      const tId = typeof l.target === "object" ? l.target.id : l.target;
      return visibleNodeIds.has(sId) && visibleNodeIds.has(tId);
    });

    const filteredNodes = visibleNodes.map((n) => ({ ...n }));
    const filteredLinks = visibleLinks.map((l) => ({
      source: typeof l.source === "object" ? l.source.id : l.source,
      target: typeof l.target === "object" ? l.target.id : l.target,
    }));

    return { nodes: filteredNodes, links: filteredLinks };
  }, [allNodes, allLinks, adjacency, hiddenTypes, searchQuery]);

  const { nodes, links } = filteredGraph;

  const toggleTypeFilter = useCallback((type) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const resetFilters = useCallback(() => {
    setHiddenTypes(new Set());
    setSearchQuery("");
  }, []);

  const getTypeLabel = useCallback(
    (type) => {
      const labelKey =
        type === "authorisation"
          ? "birdEyeAuth"
          : `birdEye${type.charAt(0).toUpperCase() + type.slice(1)}`;
      return t(`dashboard.modelView.${labelKey}`);
    },
    [t],
  );

  const selectedNodeDetails = useMemo(() => {
    if (!highlightedNode) return null;
    const selectedNode = allNodes.find((n) => n.id === highlightedNode);
    if (!selectedNode) return null;

    const connections = [];
    allLinks.forEach((l) => {
      const sId = typeof l.source === "object" ? l.source.id : l.source;
      const tId = typeof l.target === "object" ? l.target.id : l.target;
      if (sId === highlightedNode) {
        const connected = allNodes.find((n) => n.id === tId);
        if (connected)
          connections.push({ ...connected, direction: "outgoing" });
      } else if (tId === highlightedNode) {
        const connected = allNodes.find((n) => n.id === sId);
        if (connected)
          connections.push({ ...connected, direction: "incoming" });
      }
    });

    return { node: selectedNode, connections };
  }, [highlightedNode, allNodes, allLinks]);

  const handleExport = useCallback(() => {
    if (!selectedNodeDetails) return;
    const exportData = {
      selectedNode: {
        id: selectedNodeDetails.node.id,
        name: selectedNodeDetails.node.name,
        type: selectedNodeDetails.node.type,
        connections: selectedNodeDetails.node.connections,
      },
      connectedNodes: selectedNodeDetails.connections.map((c) => ({
        id: c.id,
        name: c.name,
        type: c.type,
        direction: c.direction,
        connections: c.connections,
      })),
      exportedAt: new Date().toISOString(),
    };
    const jsonStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `node-connections-${selectedNodeDetails.node.name.replace(
      /[^a-z0-9]/gi,
      "_",
    )}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [selectedNodeDetails]);

  useEffect(() => {
    if (!containerRef.current) return;
    const updateWidth = () => {
      setContainerWidth(containerRef.current?.clientWidth || 800);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (nodes.length === 0 || !svgRef.current || containerWidth === 0) return;

    const d3 = require("d3");

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const height = 600;
    const width = Math.max(containerWidth, 600);

    svg.attr("viewBox", `0 0 ${width} ${height}`);
    svg.attr("style", "max-width: 100%; height: auto; display: block;");

    const defs = svg.append("defs");

    const gridSize = 40;
    const gridPattern = defs
      .append("pattern")
      .attr("id", "grid-pattern")
      .attr("width", gridSize)
      .attr("height", gridSize)
      .attr("patternUnits", "userSpaceOnUse");
    gridPattern
      .append("rect")
      .attr("width", gridSize)
      .attr("height", gridSize)
      .attr("fill", "none");
    gridPattern
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", gridSize)
      .attr("height", gridSize)
      .attr("fill", "none")
      .attr("stroke", currentTheme?.theme?.lightGrey || "#e8e8ec")
      .attr("stroke-width", 1)
      .attr("stroke-opacity", 0.5);

    ALL_TYPES.forEach((type) => {
      const color = NODE_COLORS[type];
      const gradient = defs
        .append("radialGradient")
        .attr("id", `gradient-${type}`)
        .attr("cx", "35%")
        .attr("cy", "35%")
        .attr("r", "70%");
      gradient
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", color)
        .attr("stop-opacity", 1);
      gradient
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", color)
        .attr("stop-opacity", 0.8);
    });

    const g = svg.append("g").attr("class", "graph-content");

    const bgRectSize = Math.max(width, height) * 4;
    g.append("rect")
      .attr("x", -bgRectSize / 2)
      .attr("y", -bgRectSize / 2)
      .attr("width", bgRectSize)
      .attr("height", bgRectSize)
      .attr("fill", "url(#grid-pattern)")
      .attr("class", "grid-background")
      .style("pointer-events", "none");

    const zoom = d3
      .zoom()
      .scaleExtent([0.2, 5])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      });

    svg.call(zoom);

    const linkForce = d3
      .forceLink(links)
      .id((d) => d.id)
      .distance(70)
      .strength(0.3);

    const simulation = d3
      .forceSimulation(nodes)
      .force("link", linkForce)
      .force("charge", d3.forceManyBody().strength(-350))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force(
        "collide",
        d3.forceCollide().radius((d) => NODE_RADII[d.type] + 5),
      )
      .force("x", d3.forceX(width / 2).strength(0.05))
      .force("y", d3.forceY(height / 2).strength(0.05));

    const linkGroup = g
      .append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(links)
      .enter()
      .append("line")
      .attr("stroke", currentTheme?.theme?.mediumGrey || "#c0c0d0")
      .attr("stroke-opacity", 0.4)
      .attr("stroke-width", 1.2);

    const nodeGroup = g
      .append("g")
      .attr("class", "nodes")
      .selectAll("g")
      .data(nodes)
      .enter()
      .append("g")
      .attr("class", "node")
      .style("cursor", "pointer");

    nodeGroup
      .append("circle")
      .attr("r", (d) => Math.max(NODE_RADII[d.type], 5 + d.connections * 0.8))
      .attr("fill", (d) => `url(#gradient-${d.type})`)
      .attr("stroke", (d) => NODE_COLORS[d.type])
      .attr("stroke-width", 1.5)
      .attr("stroke-opacity", 0.6);

    const labelGroup = nodeGroup
      .filter((d) => d.connections > 0 || nodes.length < 40)
      .append("text")
      .text((d) => {
        const maxLen = 18;
        return d.name.length > maxLen
          ? d.name.substring(0, maxLen) + "..."
          : d.name;
      })
      .attr("font-size", (d) => (d.type === "agent" ? "11px" : "10px"))
      .attr("font-family", "normalFont")
      .attr("fill", currentTheme?.theme?.font_color || "#333")
      .attr("text-anchor", "middle")
      .attr(
        "dy",
        (d) => Math.max(NODE_RADII[d.type], 5 + d.connections * 0.8) + 14,
      )
      .attr("pointer-events", "none")
      .attr("opacity", 0.85);

    const tooltip = d3.select(tooltipRef.current);

    const getConnectedNodeIds = (nodeId) => {
      const connected = new Set([nodeId]);
      links.forEach((l) => {
        const sId = typeof l.source === "object" ? l.source.id : l.source;
        const tId = typeof l.target === "object" ? l.target.id : l.target;
        if (sId === nodeId) connected.add(tId);
        if (tId === nodeId) connected.add(sId);
      });
      return connected;
    };

    const typeLabelKey = (type) =>
      `dashboard.modelView.birdEye${
        type === "authorisation"
          ? "Auth"
          : type.charAt(0).toUpperCase() + type.slice(1)
      }`;

    nodeGroup
      .on("mouseover", (event, d) => {
        const connected = getConnectedNodeIds(d.id);
        nodeGroup.attr("opacity", (n) => (connected.has(n.id) ? 1 : 0.15));
        linkGroup.attr("opacity", (l) => {
          const sId = typeof l.source === "object" ? l.source.id : l.source;
          const tId = typeof l.target === "object" ? l.target.id : l.target;
          return sId === d.id || tId === d.id ? 1 : 0.05;
        });
        labelGroup.attr("opacity", (n) => (connected.has(n.id) ? 1 : 0.05));

        const typeLabel = t(typeLabelKey(d.type));
        tooltip
          .style("display", "block")
          .style("left", event.pageX + 12 + "px")
          .style("top", event.pageY - 10 + "px")
          .html(
            `<div style="font-weight:bold;font-size:13px;font-family:boldFont;color:${
              NODE_COLORS[d.type]
            };margin-bottom:2px">${d.name}</div>` +
              `<div style="font-size:11px;font-family:normalFont;color:rgba(255,255,255,0.85)">${typeLabel}</div>` +
              `<div style="font-size:11px;font-family:normalFont;color:rgba(255,255,255,0.65)">${t("dashboard.modelView.birdEyeConnections", {
                count: d.connections,
              })}</div>`,
          );
      })
      .on("mousemove", (event) => {
        tooltip
          .style("left", event.pageX + 12 + "px")
          .style("top", event.pageY - 10 + "px");
      })
      .on("mouseout", () => {
        if (!highlightedNode) {
          nodeGroup.attr("opacity", 1);
          linkGroup.attr("opacity", 0.4);
          labelGroup.attr("opacity", 0.85);
        } else {
          const connected = getConnectedNodeIds(highlightedNode);
          nodeGroup.attr("opacity", (n) => (connected.has(n.id) ? 1 : 0.15));
          linkGroup.attr("opacity", (l) => {
            const sId = typeof l.source === "object" ? l.source.id : l.source;
            const tId = typeof l.target === "object" ? l.target.id : l.target;
            return sId === highlightedNode || tId === highlightedNode
              ? 1
              : 0.05;
          });
          labelGroup.attr("opacity", (n) => (connected.has(n.id) ? 1 : 0.05));
        }
        tooltip.style("display", "none");
      })
      .on("click", (event, d) => {
        event.stopPropagation();
        if (highlightedNode === d.id) {
          setHighlightedNode(null);
          nodeGroup.attr("opacity", 1);
          linkGroup.attr("opacity", 0.4);
          labelGroup.attr("opacity", 0.85);
        } else {
          setHighlightedNode(d.id);
          const connected = getConnectedNodeIds(d.id);
          nodeGroup.attr("opacity", (n) => (connected.has(n.id) ? 1 : 0.15));
          linkGroup.attr("opacity", (l) => {
            const sId = typeof l.source === "object" ? l.source.id : l.source;
            const tId = typeof l.target === "object" ? l.target.id : l.target;
            return sId === d.id || tId === d.id ? 1 : 0.05;
          });
          labelGroup.attr("opacity", (n) => (connected.has(n.id) ? 1 : 0.05));
        }
      });

    svg.on("click", () => {
      setHighlightedNode(null);
      nodeGroup.attr("opacity", 1);
      linkGroup.attr("opacity", 0.4);
      labelGroup.attr("opacity", 0.85);
    });

    const drag = d3
      .drag()
      .on("start", (event, d) => {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on("drag", (event, d) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on("end", (event, d) => {
        if (!event.active) simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      });

    nodeGroup.call(drag);

    simulation.on("tick", () => {
      linkGroup
        .attr("x1", (d) => d.source.x)
        .attr("y1", (d) => d.source.y)
        .attr("x2", (d) => d.target.x)
        .attr("y2", (d) => d.target.y);

      nodeGroup.attr("transform", (d) => `translate(${d.x},${d.y})`);
    });

    return () => {
      simulation.stop();
      svg.selectAll("*").remove();
    };
  }, [
    nodes,
    links,
    containerWidth,
    mode,
    currentTheme?.theme?.font_color,
    currentTheme?.theme?.darkGrey,
    currentTheme?.theme?.softBackgroundColor,
    currentTheme?.theme?.lightGrey,
    currentTheme?.theme?.mediumGrey,
  ]);

  if (!hasData) {
    return (
      <DashboardPlaceholder
        mainCaption={t("dashboard.modelView.birdEyeNoData")}
        subCaption={""}
      />
    );
  }

  const legendItems = ALL_TYPES.map((type) => {
    const labelKey =
      type === "authorisation"
        ? "birdEyeAuth"
        : `birdEye${type.charAt(0).toUpperCase() + type.slice(1)}`;
    return {
      type,
      label: t(`dashboard.modelView.${labelKey}`),
      count: allNodes.filter((n) => n.type === type).length,
    };
  }).filter((item) => item.count > 0);

  const hasActiveFilters =
    hiddenTypes.size > 0 || searchQuery.trim().length > 0;
  const showingNoMatches = searchQuery.trim() && nodes.length === 0;

  const legendItemStyle = (type, isHidden) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    padding: "4px 8px",
    borderRadius: 6,
    cursor: "pointer",
    userSelect: "none",
    backgroundColor: isHidden ? "transparent" : `${NODE_COLORS[type]}15`,
    border: `1px solid ${
      isHidden
        ? currentTheme?.theme?.border_color || currentTheme?.theme?.lightGrey || "#ddd"
        : `${NODE_COLORS[type]}40`
    }`,
    opacity: isHidden ? 0.4 : 1,
    transition: "all 0.15s ease",
  });

  return (
    <View
      ref={containerRef}
      style={{
        width: "100%",
        backgroundColor:
          currentTheme?.theme?.softBackgroundColor || "#fafafa",
        borderRadius: 12,
        padding: 12,
        paddingBottom: 20,
      }}
    >
      {/* Controls row: search + legend + stats */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          marginBottom: 8,
        }}
      >
        {/* Search input row */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              position: "relative",
              flex: 1,
              minWidth: 200,
              maxWidth: 400,
            }}
          >
            <TextInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder={t("dashboard.modelView.birdEyeSearch")}
              placeholderTextColor={currentTheme?.theme?.darkGrey || "#999"}
              style={{
                height: 34,
                paddingHorizontal: 12,
                paddingRight: 32,
                fontSize: 13,
                fontFamily: "normalFont",
                color: currentTheme?.theme?.font_color,

                borderRadius: 8,
                borderWidth: 1,
                borderColor:
                  currentTheme?.theme?.border_color ||
                  currentTheme?.theme?.lightGrey ||
                  "#e0e0e0",
                outline: "none",
              }}
            />
            {searchQuery.trim() && (
              <TouchableOpacity
                onPress={() => setSearchQuery("")}
                style={{
                  position: "absolute",
                  right: 4,
                  top: 4,
                  width: 26,
                  height: 26,
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: 4,
                }}
              >
                <AppText
                  style={{
                    fontSize: 16,
                    color: currentTheme?.theme?.darkGrey,
                  }}
                >
                  ×
                </AppText>
              </TouchableOpacity>
            )}
          </div>

          <div style={{ marginLeft: "auto" }}>
            <AppText
              style={{
                fontSize: 11,
                color: currentTheme?.theme?.darkGrey,
                fontStyle: "italic",
              }}
            >
              {t("dashboard.modelView.birdEyeFiltered", {
                count: nodes.length,
                total: allNodes.length,
              })}
              {" · "}
              {nodes.length} nodes · {links.length} edges
            </AppText>
          </div>
        </div>

        {/* Interactive legend row */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          <AppText
            style={{
              fontSize: 12,
              fontWeight: "bold",
              color: currentTheme?.theme?.font_color,
            }}
          >
            {t("dashboard.modelView.birdEyeLegend")}:
          </AppText>
          {legendItems.map((item) => {
            const isHidden = hiddenTypes.has(item.type);
            return (
              <div
                key={item.type}
                style={legendItemStyle(item.type, isHidden)}
                onClick={() => toggleTypeFilter(item.type)}
              >
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: isHidden
                      ? "transparent"
                      : NODE_COLORS[item.type],
                    border: `2px solid ${NODE_COLORS[item.type]}`,
                    flexShrink: 0,
                  }}
                />
                <AppText
                  style={{
                    fontSize: 11,
                    color: isHidden
                      ? currentTheme?.theme?.darkGrey
                      : currentTheme?.theme?.font_color,
                    fontWeight: isHidden ? "normal" : "500",
                  }}
                >
                  {item.label}
                </AppText>
                <AppText
                  style={{
                    fontSize: 10,
                    color: currentTheme?.theme?.darkGrey,
                    marginLeft: 2,
                  }}
                >
                  ({item.count})
                </AppText>
              </div>
            );
          })}
          {hasActiveFilters && (
            <TouchableOpacity
              onPress={resetFilters}
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 4,
                paddingHorizontal: 10,
                paddingVertical: 6,
                borderRadius: 6,
                backgroundColor: currentTheme?.theme?.primary,
                borderWidth: 1,
                borderColor: currentTheme?.theme?.primary,
              }}
            >
              <AppText
                style={{
                  fontSize: 12,
                  color: currentTheme?.theme?.white,
                  fontWeight: "600",
                }}
              >
                {t("dashboard.modelView.birdEyeResetFilters")}
              </AppText>
            </TouchableOpacity>
          )}
        </div>
      </div>

      {/* Graph or no-matches message */}
      {showingNoMatches ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
            width: "100%",
          }}
        >
          <AppText
            style={{
              fontSize: 14,
              color: currentTheme?.theme?.darkGrey,
              fontStyle: "italic",
            }}
          >
            {t("dashboard.modelView.birdEyeNoMatches")}
          </AppText>
        </div>
      ) : (
        <div
          style={{
            position: "relative",
            width: "100%",
            borderRadius: 8,
            overflow: "hidden",
            border: `1px solid ${currentTheme?.theme?.border_color || currentTheme?.theme?.lightGrey || "#e0e0e0"}`,
            cursor: "pointer",
          }}
        >
          <svg
            ref={svgRef}
            style={{
              width: "100%",
              minHeight: 600,
              backgroundColor:
                currentTheme?.theme?.backgroundColor || "#fff",
            }}
          />
            <div
            ref={tooltipRef}
            style={{
              position: "fixed",
              display: "none",
              padding: "8px 10px",
              fontFamily: "normalFont",
              backgroundColor:
                currentTheme?.theme?.tooltipBackgroundColor ||
                currentTheme?.theme?.cardBackground ||
                "#fff",
              border: `1px solid ${currentTheme?.theme?.border_color || currentTheme?.theme?.lightGrey || "#ddd"}`,
              borderRadius: 6,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              pointerEvents: "none",
              zIndex: 1000,
              maxWidth: 250,
            }}
          />
        </div>
      )}

      {/* Connection details panel */}
      <div
        style={{
          marginTop: 12,
          borderRadius: 8,
          overflow: "hidden",
          border: `1px solid ${currentTheme?.theme?.border_color || currentTheme?.theme?.lightGrey || "#e0e0e0"}`,
          backgroundColor: currentTheme?.theme?.cardBackground || "#fff",
        }}
      >
        {selectedNodeDetails ? (
          <div style={{ width: "100%" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 14px",
                borderBottom: `1px solid ${currentTheme?.theme?.border_color || currentTheme?.theme?.lightGrey || "#e8e8ec"}`,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: NODE_COLORS[selectedNodeDetails.node.type],
                    flexShrink: 0,
                  }}
                />
                <AppText
                  style={{
                    fontSize: 14,
                    fontWeight: "bold",
                    color: currentTheme?.theme?.font_color,
                  }}
                >
                  {selectedNodeDetails.node.name}
                </AppText>
                <div
                  style={{
                    padding: "2px 8px",
                    borderRadius: 4,
                    backgroundColor: `${NODE_COLORS[selectedNodeDetails.node.type]}20`,
                  }}
                >
                  <AppText
                    style={{
                      fontSize: 10,
                      color: NODE_COLORS[selectedNodeDetails.node.type],
                      fontWeight: "600",
                    }}
                  >
                    {getTypeLabel(selectedNodeDetails.node.type)}
                  </AppText>
                </div>
              </div>
              <TouchableOpacity
                onPress={handleExport}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 5,
                  paddingHorizontal: 12,
                  paddingVertical: 7,
                  borderRadius: 6,
                  backgroundColor: currentTheme?.theme?.primary || "#2563eb",
                }}
              >
                <AppText
                  style={{
                    fontSize: 12,
                    color: "#fff",
                    fontWeight: "600",
                  }}
                >
                  {t("dashboard.modelView.birdEyeExport")}
                </AppText>
              </TouchableOpacity>
            </div>

            <div
              style={{
                maxHeight: 280,
                overflowY: "auto",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                  fontFamily: "normalFont",
                }}
              >
                <thead>
                  <tr
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor:
                        currentTheme?.theme?.softBackgroundColor ||
                        currentTheme?.theme?.lightGrey ||
                        "#f5f5f8",
                      zIndex: 1,
                    }}
                  >
                    {[
                      { key: "name", label: t("dashboard.modelView.birdEyeColName") },
                      { key: "type", label: t("dashboard.modelView.birdEyeColType") },
                      { key: "id", label: t("dashboard.modelView.birdEyeColId") },
                      { key: "direction", label: t("dashboard.modelView.birdEyeColDirection") },
                      { key: "connections", label: t("dashboard.modelView.birdEyeColConn") },
                    ].map((col) => (
                      <th
                        key={col.key}
                        style={{
                          textAlign: "left",
                          padding: "8px 12px",
                          fontWeight: "600",
                          fontSize: 11,
                          color: currentTheme?.theme?.darkGrey || "#666",
                          borderBottom: `1px solid ${currentTheme?.theme?.border_color || currentTheme?.theme?.lightGrey || "#e8e8ec"}`,
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                        }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selectedNodeDetails.connections.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: currentTheme?.theme?.darkGrey || "#999",
                          fontStyle: "italic",
                        }}
                      >
                        {t("dashboard.modelView.birdEyeNoConnections")}
                      </td>
                    </tr>
                  ) : (
                    selectedNodeDetails.connections.map((conn, idx) => (
                      <tr
                        key={conn.id + idx}
                        style={{
                          backgroundColor:
                            idx % 2 === 0
                              ? "transparent"
                              : currentTheme?.theme?.softBackgroundColor ||
                                currentTheme?.theme?.lightGrey ||
                                "#fafafc",
                        }}
                      >
                        <td
                          style={{
                            padding: "7px 12px",
                            color: currentTheme?.theme?.font_color,
                          }}
                        >
                          {conn.name}
                        </td>
                        <td style={{ padding: "7px 12px" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                            }}
                          >
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: NODE_COLORS[conn.type],
                                flexShrink: 0,
                              }}
                            />
                            <AppText
                              style={{
                                fontSize: 11,
                                color: currentTheme?.theme?.darkGrey || "#666",
                              }}
                            >
                              {getTypeLabel(conn.type)}
                            </AppText>
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "7px 12px",
                            fontFamily: "boldFont",
                            fontSize: 10,
                            color: currentTheme?.theme?.mediumGrey || "#888",
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {conn.id}
                        </td>
                        <td style={{ padding: "7px 12px" }}>
                          <AppText
                            style={{
                              fontSize: 11,
                              color:
                                conn.direction === "outgoing"
                                  ? "#16a34a"
                                  : "#d97706",
                              fontWeight: "500",
                            }}
                          >
                            {conn.direction === "outgoing"
                              ? t("dashboard.modelView.birdEyeDirOutgoing")
                              : t("dashboard.modelView.birdEyeDirIncoming")}
                          </AppText>
                        </td>
                        <td
                          style={{
                            padding: "7px 12px",
                            color: currentTheme?.theme?.darkGrey || "#666",
                            fontSize: 11,
                          }}
                        >
                          {conn.connections}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "28px 20px",
              gap: 6,
            }}
          >
            <AppText
              style={{
                fontSize: 13,
                color: currentTheme?.theme?.darkGrey || "#999",
                fontStyle: "italic",
                textAlign: "center",
              }}
            >
              {t("dashboard.modelView.birdEyeSelectPrompt")}
            </AppText>
          </div>
        )}
      </div>
    </View>
  );
};

export default BirdsEyeView;
